import styled from "@emotion/styled";
import { useCallback, useEffect, useRef, useState } from "react";
import { CgSearchLoading } from "react-icons/cg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button as AIMEButton } from "tot-loft-component-library";
import GenericModal from "./GenericModal";
import ModalAdminAddError from "./ModalAdminAddError";
import ModalAdminvendorsone from "./ModalAdminvendorsone";
import ModalAdminvendorstatus from "./ModalAdminvendorstatus";
import Navbar from "./Navbar";
import * as PRTNR from "./prtnr";
import { fetchIndustrysectorparentItems } from "./redux/slices/IndustrysectorcategoriesThunks";
import { resetPagination } from "./redux/slices/VendorSlice";
import { fetchVendorItems } from "./redux/slices/VendorThunks";

export default function Adminvendorsone() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const vendor = useSelector((state) => state.vendor.items);
  const loading = useSelector((state) => state.vendor.loading);
  const loadingMore = useSelector((state) => state.vendor.loadingMore);
  const hasMore = useSelector((state) => state.vendor.hasMore);
  const currentPage = useSelector((state) => state.vendor.currentPage);
  const industryParent = useSelector(
    (state) => state.industrysectorparent.items
  );

  const [showData, setShowData] = useState([]);
  const [tableColumn, setTableColumn] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openStatusModal, setopenStatusModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sector, setSector] = useState([]);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const loadingRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const openModal = () => setIsModalOpen(true);
  const openErrorModal = () => setIsErrorModalOpen(true);
  const closeErrorModal = () => setIsErrorModalOpen(false);
  const closeModal = () => {
    getVendors();
    setIsModalOpen(false);
  };

  const openStatusEditModal = () => {
    setopenStatusModal(true);
  };

  const closeStatusEditModal = async () => {
    await getVendors();
    setopenStatusModal(false);
  };

  const actionHandler = (item) => {
    const singleVendor = vendor.find((val) => val._id === item._id);
    navigate(`/adminvendorsone/${singleVendor._id}`, {
      state: { sector: sector },
    });
  };

  const editClick = (item) => {
    const data = {
      _id: item?._id,
      vendor_profile_status: item?.status,
    };
    setSelectedItem(data);
    openStatusEditModal();
  };

  // Load more data for infinite scroll
  const loadMoreData = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      dispatch(
        fetchVendorItems({
          page: currentPage + 1,
          search: searchDebounced,
          limit: 20,
          reset: false,
        })
      );
    }
  }, [dispatch, currentPage, searchDebounced, hasMore, loadingMore, loading]);

  // Process vendor data for table display
  useEffect(() => {
    if (industryParent.length > 0) {
      const arr = industryParent.map((val) => ({
        value: val.sector_name,
        label: val.sector_name,
      }));
      setSector(arr);
    }

    if (vendor.length > 0) {
      const tableColumns = [
        { key: "name", width: "20%" },
        { key: "email", width: "20%" },
        { key: "industry", width: "20%" },
        { key: "status", width: "20%", align: "center" },
        { key: "action", width: "20%" },
      ];

      const tableData = vendor.map((val) => ({
        _id: val?._id,
        name: val.company_name || "",
        email: val.vendor_work_email
          ? val.vendor_work_email.length > 16
            ? val.vendor_work_email.slice(0, 16) + "..."
            : val.vendor_work_email
          : "",
        industry: Array.isArray(val.vendor_industry_sector)
          ? val.vendor_industry_sector
          : [],
        status: val?.vendor_profile_status || "",
      }));

      setTableColumn(tableColumns);
      setShowData(tableData);
    }
  }, [vendor, industryParent]);

  const getVendors = async () => {
    try {
      dispatch(fetchIndustrysectorparentItems());
      dispatch(resetPagination());
      dispatch(
        fetchVendorItems({
          page: 1,
          search: searchDebounced,
          limit: 20,
          reset: true,
        })
      );
    } catch (e) {
      console.log(e, "error");
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchDebounced(search);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Initial data load
  useEffect(() => {
    dispatch(fetchVendorItems({ page: 1, search: "", limit: 20, reset: true }));
    dispatch(fetchIndustrysectorparentItems());

    const tableHeaders = [
      { label: "Vendor Name", width: "20%" },
      { label: "Email", width: "20%" },
      { label: "Industry Sector", width: "20%" },
      { label: "Profile Status", width: "20%", align: "center" },
      { label: "Action", width: "20%" },
    ];
    setTableHeader(tableHeaders);
  }, [dispatch]);

  // Handle search change - fetch new data
  useEffect(() => {
    dispatch(resetPagination());
    dispatch(
      fetchVendorItems({
        page: 1,
        search: searchDebounced,
        limit: 20,
        reset: true,
      })
    );
  }, [searchDebounced, dispatch]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreData();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [loadMoreData, hasMore, loadingMore, loading]);

  return (
    <RootWrapperAdminvendorsone>
      <Navbar />
      <Dashboard>
        <Wrapper>
          <Left_0001>
            <PRTNR.SidebarNav />
          </Left_0001>
          <Right>
            <DashboardHeader>
              <Frame1462>
                <WelcomeJames>Vendors</WelcomeJames>
              </Frame1462>
              <Frame1461>
                <AIMEButton
                  size="small"
                  onClick={() => {
                    navigate("/add-vendor", { state: { sector: sector } });
                  }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                    flex: "none",
                    gap: "10px",
                    borderRadius: "48px",
                    background: "rgb(33, 37, 67)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    boxSizing: "border-box",
                    padding: "16px 32px",
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      textOverflow: "ellipsis",
                      fontSize: "16px",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "600",
                      lineHeight: "110%",
                      textAlign: "left",
                    }}
                  >
                    Add Vendor
                  </span>
                </AIMEButton>
              </Frame1461>
            </DashboardHeader>
            <ContentBox>
              <TableTitle>
                <Frame1431>
                  <Title>All</Title>
                </Frame1431>
                <Frame1432>
                  <GFieldsStacked>
                    <GFieldsStacked_0001>
                      <PRTNR.Input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </GFieldsStacked_0001>
                  </GFieldsStacked>
                </Frame1432>
              </TableTitle>

              {loading && vendor.length === 0 ? (
                <span>Loading vendors...</span>
              ) : (
                <>
                  <PRTNR.TableComponent
                    data={showData}
                    tableColumn={tableColumn}
                    tableHeader={tableHeader}
                    viewClick={actionHandler}
                    editClick={editClick}
                  />

                  {/* Loading indicator for infinite scroll */}
                  {/* <div
                    ref={loadingRef}
                    style={{ height: "20px", margin: "20px 0" }}
                  >
                    {loadingMore && <span>Loading more...</span>}
                  </div>

                  {!hasMore && vendor.length > 0 && (
                    <EndMessage>No more vendors to load</EndMessage>
                  )} */}
                </>
              )}
            </ContentBox>
          </Right>
        </Wrapper>
      </Dashboard>
      <Footer>
        <Section>
          <Section_0001>
            <Wrapper_0001>
              <Frame38>
                <_2025PaartnerLimitedAllRightsReserved>
                  © 2025 Paartner Limited. All Rights Reserved
                </_2025PaartnerLimitedAllRightsReserved>
              </Frame38>
            </Wrapper_0001>
          </Section_0001>
        </Section>
      </Footer>

      <GenericModal isOpen={isModalOpen} onClose={closeModal}>
        <ModalAdminvendorsone
          isOpen={isModalOpen}
          onClose={closeModal}
          sector={sector}
          errorModal={openErrorModal}
        />
      </GenericModal>
      <GenericModal
        isOpen={openStatusModal}
        onClose={() => setopenStatusModal(!openStatusModal)}
      >
        <ModalAdminvendorstatus
          isOpen={openStatusEditModal}
          onClose={closeStatusEditModal}
          data={selectedItem}
          setData={setSelectedItem}
          oldData={selectedItem}
          setOldData={setSelectedItem}
        />
      </GenericModal>
      <GenericModal
        isOpen={isErrorModalOpen}
        onClose={closeErrorModal}
        bg="rgb(226, 232, 240)"
      >
        <ModalAdminAddError
          isOpen={isErrorModalOpen}
          onClose={closeErrorModal}
        />
      </GenericModal>
    </RootWrapperAdminvendorsone>
  );
}

const RootWrapperAdminvendorsone = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  width: 1200px;
  box-sizing: border-box;
  padding: 32px 0px;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 298px;
  box-sizing: border-box;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  width: 878px;
  box-sizing: border-box;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 707px;
  box-sizing: border-box;
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: center;
`;

const Frame1461 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const ContentBox = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const TableTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1431 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Title = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 20px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1432 = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 782px;
  box-sizing: border-box;
`;

const GFieldsStacked = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;

const GFieldsStacked_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 4px;
  box-sizing: border-box;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  color: rgb(33, 37, 67);
  font-family: Montserrat, sans-serif;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const EndMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: rgb(100, 100, 100);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Section = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Section_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame38 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const _2025PaartnerLimitedAllRightsReserved = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;
